import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Zap, Database, Key, Shield, List, ArrowRight, ChevronDown, ChevronUp,
  HelpCircle, Settings, AlertCircle, CheckCircle, Play
} from 'lucide-react';
import { useTour } from '../tour/useTour';

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

function Section({ icon, title, children }: SectionProps) {
  const [open, setOpen] = useState(true);
  return (
    <div className="pk-card pk-card-flush">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="pk-card-head"
        style={{ width: '100%', textAlign: 'left', border: 0, background: 'none', cursor: 'pointer', borderBottom: open ? undefined : 0 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="pk-i-plate">{icon}</div>
          <h2 className="pk-h4">{title}</h2>
        </div>
        <div className="actions">
          {open ? <ChevronUp size={16} className="pk-i" /> : <ChevronDown size={16} className="pk-i" />}
        </div>
      </button>
      {open && <div className="pk-card-body">{children}</div>}
    </div>
  );
}

function Tag({ children, variant = 'pk-badge-accent' }: { children: React.ReactNode; variant?: string }) {
  return <span className={`pk-badge ${variant}`}>{children}</span>;
}

function InfoBox({ children, type = 'info' }: { children: React.ReactNode; type?: 'info' | 'warn' | 'tip' }) {
  const cls = { info: 'pk-note-accent', warn: 'pk-note-warning', tip: '' }[type];
  const style = type === 'tip' ? { background: 'var(--arc-success-soft)' } : undefined;
  const icon = type === 'tip' ? <CheckCircle size={14} style={{ color: 'var(--arc-success)' }} /> : <AlertCircle size={14} />;
  return (
    <div className={`pk-note ${cls}`} style={{ marginTop: 12, ...style }}>
      {icon}
      <span className="pk-small">{children}</span>
    </div>
  );
}

export default function Guide() {
  const subscriptionsEnabled = process.env.REACT_APP_ENABLE_SUBSCRIPTIONS !== 'false';
  const { startTour } = useTour({ subscriptionsEnabled, autoStart: false });

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Header */}
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <HelpCircle size={22} style={{ color: "var(--arc-accent)" }} />
          <h1 className="pk-h1">How It All Works</h1>
        </div>
        <p className="pk-sub">
          A guide to the key concepts — Task Determination, Models, Lists, Categories, and how API key settings tie them together.
        </p>
        <button type="button" onClick={startTour} className="pk-btn pk-btn-ink" style={{ marginTop: 12 }}>
          <Play size={14} />
          Take the tour
        </button>
      </div>

      {/* Flow overview */}
      <div className="pk-card pk-card-grad-top">
        <h2 className="pk-h4" style={{ marginBottom: 16 }}>Request flow — what happens when an API call arrives</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          {[
            { label: 'API Key', sub: 'authenticated' },
            { label: 'Task Determination', sub: 'query classified' },
            { label: 'Model Pool', sub: 'filtered by key settings' },
            { label: 'Category Tags', sub: 'further narrows pool' },
            { label: 'Best Model', sub: 'picked by priority' },
            { label: 'Response', sub: 'streamed back' },
          ].map((step, i, arr) => (
            <React.Fragment key={step.label}>
              <div className="pk-card-inset" style={{ textAlign: 'center', minWidth: 100 }}>
                <div style={{ fontWeight: 700, fontSize: 12.5, color: 'var(--arc-ink)' }}>{step.label}</div>
                <div className="pk-small" style={{ marginTop: 2 }}>{step.sub}</div>
              </div>
              {i < arr.length - 1 && <ArrowRight size={14} className="pk-i" style={{ flexShrink: 0 }} />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Task Determination */}
      <Section
        icon={<Zap size={16} />}
        title="Task Determination (TD)"
      >
        <p className="pk-body" style={{ marginBottom: 12 }}>
          Before routing a query to an AI model, the system runs a lightweight analysis step called <strong>Task Determination</strong>.
          A dedicated TD model reads the query and returns a structured classification:
        </p>
        <div className="pk-grid pk-grid-2" style={{ marginBottom: 12 }}>
          {[
            { field: 'taskType', desc: 'e.g. coding, analysis, creative, math' },
            { field: 'complexity', desc: '1–10 score' },
            { field: 'requiresPrivacy', desc: 'true / false — triggers privacy-only model pool' },
            { field: 'requestType', desc: 'streaming or sync hint' },
            { field: 'customTags', desc: 'your own boolean tags (e.g. marketing: true)' },
          ].map(({ field, desc }) => (
            <div key={field} className="pk-card-inset">
              <code className="pk-mono" style={{ color: "var(--arc-accent)" }}>{field}</code>
              <p className="pk-small" style={{ marginTop: 2 }}>{desc}</p>
            </div>
          ))}
        </div>
        <p className="pk-body" style={{ marginBottom: 4 }}>
          The TD model you configure under <Link to="/models" style={{ color: "var(--arc-accent)" }}>Models</Link> drives
          this step. Mark a model as <Tag>Task Determination</Tag> and <Tag variant="pk-badge-success">Privacy Compliant</Tag> — the
          highest-ranked one matching both criteria is used.
        </p>
        <InfoBox type="warn">
          Small models (e.g. GPT-4o-mini) follow a "when in doubt → false" rule for privacy detection. If accuracy matters, configure a more
          capable TD model (GPT-4o or equivalent) at a low rank number.
        </InfoBox>
      </Section>

      {/* Models */}
      <Section
        icon={<Database size={16} />}
        title="Models"
      >
        <p className="pk-body" style={{ marginBottom: 12 }}>
          Under <Link to="/models" style={{ color: "var(--arc-accent)" }}>Models</Link> you manage the AI models available to your API keys.
          Each model has several flags that control when it's selected:
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
          {[
            { flag: 'ranked_position', detail: 'Lower number = higher priority. Unranked defaults to 999. This is the primary sort key for model selection.' },
            { flag: 'isPrivacyCompliant', detail: 'When a query is detected as requiring privacy, only models with this flag are eligible.' },
            { flag: 'taskDeterminationModel', detail: 'Marks the model as a TD model. The system picks the highest-ranked TD + privacy-compliant model for analysis.' },
            { flag: 'customTagSupport', detail: 'A per-tag boolean map (e.g. { marketing: true }). When a custom tag fires and filtering is enabled, only models with that tag set to true are used.' },
          ].map(({ flag, detail }) => (
            <div key={flag} className="pk-card-inset" style={{ display: "flex", gap: 12 }}>
              <code className="pk-mono pk-small" style={{ color: "var(--arc-accent)", flexShrink: 0, marginTop: 2 }}>{flag}</code>
              <p className="pk-small">{detail}</p>
            </div>
          ))}
        </div>
        <InfoBox type="info">
          Model selection always respects the API key's <strong>selection mode</strong> (all / custom_only / global_only / privacy_only) and
          <strong> priority</strong> (rank vs price) before applying any further filtering.
        </InfoBox>
      </Section>

      {/* Model Lists */}
      <Section
        icon={<List size={16} />}
        title="Model Lists"
      >
        <p className="pk-body" style={{ marginBottom: 12 }}>
          A <strong>Model List</strong> is a static, curated set of models you create under the <em>Lists</em> tab on the{' '}
          <Link to="/models" style={{ color: "var(--arc-accent)" }}>Models</Link> page.
          Assigning a list to an API key restricts that key to <em>only</em> using models in the list.
        </p>
        <div className="pk-body" style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
          <p>The key's <strong>selection mode</strong> and <strong>priority</strong> still apply <em>within</em> the list — so if the list has 5 models and the mode is <Tag>privacy_only</Tag>, only privacy-compliant models from that 5 are candidates.</p>
          <p>This is useful for white-labelling or cost control: give a client key a list with only approved, cost-effective models.</p>
        </div>
        <InfoBox type="warn">
          If no models in the list match the active filters (mode + privacy requirement), the request fails. Always ensure your list has at least one eligible model for each scenario you expect.
        </InfoBox>
      </Section>

      {/* Category Rules / Custom Tags */}
      <Section
        icon={<Shield size={16} />}
        title="Category Rules & Custom Tags"
      >
        <p className="pk-body" style={{ marginBottom: 12 }}>
          Under <Link to="/category-rules" style={{ color: "var(--arc-accent)" }}>Category Rules</Link> you define boolean detection tags
          that TD evaluates on every query. There are two kinds:
        </p>
        <div className="pk-grid pk-grid-2" style={{ marginBottom: 16 }}>
          <div className="pk-card-inset">
            <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
              <Tag variant="pk-badge-success">Privacy</Tag>
              <span className="pk-small">built-in</span>
            </div>
            <p className="pk-small">Always active. When <code>requiresPrivacy: true</code> is detected, the model pool is restricted to privacy-compliant models. Cannot be disabled.</p>
          </div>
          <div className="pk-card-inset">
            <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
              <Tag>Custom Tags</Tag>
              <span className="pk-small">e.g. marketing, legal</span>
            </div>
            <p className="pk-small">You define the rules. TD always detects them, but model pool filtering only activates on keys where the tag slug is enabled in <em>Tag-Based Filtering</em>.</p>
          </div>
        </div>
        <p className="pk-body" style={{ marginBottom: 4 }}>
          Each category has a <strong>Configure Rules</strong> tab (write the detection rules in plain text — what counts as true vs false) and a <strong>Test Center</strong> tab to verify detection with sample queries.
        </p>
        <InfoBox type="tip">
          Custom tags and Model Lists are complementary. A list gives a static model pool per key; a custom tag dynamically narrows that pool based on what's in the query. Use both together for fine-grained control.
        </InfoBox>
      </Section>

      {/* API Key Settings */}
      <Section
        icon={<Key size={16} />}
        title="API Key Settings"
      >
        <p className="pk-body" style={{ marginBottom: 12 }}>
          Each API key has five configurable settings that interact with the above systems:
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 12 }}>
          {[
            {
              setting: 'Force Task Determination',
              values: ['on (default)', 'off'],
              detail: 'When on, TD always runs and the system picks the best model — any model passed in the request body is ignored. When off, a caller-specified model is used directly (no TD cost). If no model is specified and TD is off, TD still runs automatically to select the best model.',
            },
            {
              setting: 'Model Selection Mode',
              values: ['all', 'custom_only', 'global_only', 'privacy_only'],
              detail: 'The first coarse filter — determines which model pool is eligible before any ranking or tag filtering.',
            },
            {
              setting: 'Model Selection Priority',
              values: ['rank', 'price'],
              detail: 'Within the eligible pool, rank sorts by ranked_position (quality); price sorts by cost per token (cheapest first).',
            },
            {
              setting: 'Model List',
              values: ['optional'],
              detail: 'Pins the key to a curated list. Selection mode and priority still apply within the list.',
            },
            {
              setting: 'Tag-Based Filtering',
              values: ['per-slug checkboxes'],
              detail: 'Opt-in enforcement gate for custom tags. TD always detects tags, but only slugs ticked here actually filter the model pool for this key.',
            },
          ].map(({ setting, values, detail }) => (
            <div key={setting} className="pk-card-inset">
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <span style={{ fontWeight: 700, fontSize: 12.5, color: "var(--arc-ink)" }}>{setting}</span>
                {values.map(v => <Tag key={v} variant="">{v}</Tag>)}
              </div>
              <p className="pk-small">{detail}</p>
            </div>
          ))}
        </div>
        <InfoBox type="info">
          Tag-Based Filtering is API key requests only. JWT (browser) users get <code>customTags</code> in the task analysis result but no model pool restriction — that's by design.
        </InfoBox>
      </Section>

      {/* Quick reference */}
      <Section
        icon={<Settings size={16} />}
        title="Quick Setup Reference"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }} className="pk-body">
          {[
            {
              goal: 'Route privacy-sensitive queries to compliant models only',
              steps: ['Mark models as Privacy Compliant in Models', 'Privacy detection is always on — no key config needed'],
            },
            {
              goal: 'Use a specific subset of models for a key',
              steps: ['Create a Model List with the desired models', 'Assign the list to the key in API Key Settings'],
            },
            {
              goal: 'Route marketing queries to marketing-capable models',
              steps: ['Create a "Marketing" category in Category Rules', 'Write detection rules and test them', 'Tick "Marketing" in each capable model\'s Custom Tags', 'Enable "Marketing" in the key\'s Tag-Based Filtering'],
            },
            {
              goal: 'Let callers choose their own model (bypass TD)',
              steps: ['Uncheck Force Task Determination in the key\'s settings', 'Caller passes a model in the request — it is used directly, no TD cost (quota not consumed)', 'If caller sends no model and TD is off, TD still runs to pick the best model — full TD cost applies'],
            },
            {
              goal: 'Use your own AI backend for task determination',
              steps: ['Add a custom endpoint model in Models', 'Tick Task Determination Model + Privacy Compliant', 'Set ranked_position to a low number (e.g. 10) so it wins'],
            },
          ].map(({ goal, steps }) => (
            <div key={goal} className="pk-card-inset">
              <p style={{ fontWeight: 700, fontSize: 12.5, color: "var(--arc-ink)", marginBottom: 8 }}>{goal}</p>
              <ol style={{ display: 'flex', flexDirection: 'column', gap: 4, listStyle: 'decimal', paddingLeft: 18 }}>
                {steps.map(s => <li key={s} className="pk-small">{s}</li>)}
              </ol>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
